import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {SignatureTemplateModel} from '../../../models/api/signatureModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum} from '../../../models/url/settingsUrlModel';
import {AppState} from '../../../store/appState';
import {
  deleteSignatureAsyncAction,
  updateSignatureAction,
} from '../../../store/data/signatures/signaturesActions';
import {getSignatureById} from '../../../store/data/signatures/signaturesSelectors';
import {SettingsBannersEnum, useSettingsBanners} from '../../banners/settingsBannersContext';
import {SignaturesError} from '../signaturesHelpers';
import {SignatureForm, SignatureFormInheritableProps} from './signatureForm';

interface SignatureEditFormProps extends SignatureFormInheritableProps {
  signatureId: number;
}

export const SignatureEditForm: FC<SignatureEditFormProps> = (props) => {
  const {signaturesUrl, signatureId, panelId} = props;
  const dispatch = useAppDispatch();
  const {navigateToLink} = useLinkNavigation();
  const currentSignature = useAppSelector((state: AppState) => getSignatureById(state, signatureId));

  const {showBanner} = useSettingsBanners();

  const deleteSignature = async () => {
    try {
      await dispatch(deleteSignatureAsyncAction(signatureId));
      showBanner({
        type: SettingsBannersEnum.SUCCESS,
        message: (
          <Trans id="settings-signatures-delete-success">
            Signature &quot;{currentSignature?.name || ''}&quot; has been deleted
          </Trans>
        ),
      });
    } catch (err) {
      if (err instanceof SignaturesError) {
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: err.message,
        });
      }
    }
  };

  const navigateToSignatureOverview = () => {
    const signaturesOverviewUrl = buildSettingsPath({
      ...signaturesUrl,
      resourcePage: SettingsResourcePagesEnum.OVERVIEW,
      signatureId: undefined,
    });
    navigateToLink(signaturesOverviewUrl);
  };

  const handleSubmit = async (signature: SignatureTemplateModel) => {
    try {
      await dispatch(updateSignatureAction(signatureId, signature));
      showBanner({
        type: SettingsBannersEnum.SUCCESS,
        message: (
          <Trans id="settings-signatures-update-success">
            Signature &quot;{signature.name}&quot; has been updated
          </Trans>
        ),
      });
      navigateToSignatureOverview();
    } catch (err) {
      if (err instanceof SignaturesError) {
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: err.message,
        });
      }
    }
  };

  return (
    <SignatureForm
      onSubmit={handleSubmit}
      signaturesUrl={signaturesUrl}
      isEditing
      signature={currentSignature}
      panelId={panelId}
      deleteSignature={deleteSignature}
    />
  );
};
